import React from "react";
import CONTENT from "./tourContent";

const StepContent = ({ title, body, tip }) => (
  <div className="ts-tour-step">
    <h6 className="ts-tour-step__title">{title}</h6>
    <p className="ts-tour-step__body">{body}</p>
    {tip && <p className="ts-tour-step__tip">💡 {tip}</p>}
  </div>
);

const closeSidebar = () => {
  const mainMenu = document.querySelector(".main-menu-sidebar.open");
  if (mainMenu) document.querySelector(".sidebar-toggle-btn")?.click();
};

/**
 * Build the tour steps array.
 * @param {string} lang - "de" | "en" | "it"
 * @param {function} navigate - React Router navigate function
 */
export function createTourSteps(lang, navigate, persona = "tree_inspector") {
  const c = CONTENT[lang] || CONTENT["en"];
  const personaStartMap = {
    tree_inspector: {
      route: "/trees",
      titleKey: "nav_trees_title",
      bodyKey: "nav_trees_body",
    },
    irrigation_team: {
      route: "/tasks",
      titleKey: "nav_tasks_title",
      bodyKey: "nav_tasks_body",
    },
    city_tree_manager: {
      route: "/dashboard",
      titleKey: "nav_dashboard_title",
      bodyKey: "nav_dashboard_body",
    },
    research_partner: {
      route: "/data-science",
      titleKey: "nav_datascience_title",
      bodyKey: "nav_datascience_body",
    },
    treesense_support: {
      route: "/sensors",
      titleKey: "nav_sensors_title",
      bodyKey: "nav_sensors_body",
    },
  };
  const startStepConfig = personaStartMap[persona] || personaStartMap.tree_inspector;

  // Helper: page transition step – closes sidebar then navigates
  const navStep = (route, titleKey, bodyKey) => ({
    selector: "body",
    bypassElem: true,
    position: "center",
    action: () => {
      closeSidebar();
      navigate(route);
    },
    content: <StepContent title={c[titleKey]} body={c[bodyKey]} />,
  });

  // Helper: element step with optional route guard for back-navigation
  const step = (selector, titleKey, bodyKey, route = null) => ({
    selector,
    content: <StepContent title={c[titleKey]} body={c[bodyKey]} />,
    ...(route
      ? {
          action: () => {
            if (window.location.pathname !== route) {
              closeSidebar();
              navigate(route);
            }
          },
        }
      : {}),
  });

  return [
    // 0 – Welcome
    {
      selector: "body",
      bypassElem: true,
      position: "center",
      content: <StepContent title={c.welcome_title} body={c.welcome_body} tip={c.welcome_tip} />,
    },

    // 1 – Persona-specific start
    navStep(startStepConfig.route, startStepConfig.titleKey, startStepConfig.bodyKey),

    // 2 – Dashboard KPI overview
    step(
      '[data-tour="dashboard-content"]',
      "dashboard_overview_title",
      "dashboard_overview_body",
      "/dashboard"
    ),

    // 3 – Baumbestand
    step(
      '[data-tour="dashboard-inventory"]',
      "dashboard_inventory_title",
      "dashboard_inventory_body",
      "/dashboard"
    ),

    // 4 – Biometrie
    step(
      '[data-tour="dashboard-biometrics"]',
      "dashboard_biometrics_title",
      "dashboard_biometrics_body",
      "/dashboard"
    ),

    // 5 – Sensorik & Status
    step(
      '[data-tour="dashboard-sensors"]',
      "dashboard_sensors_title",
      "dashboard_sensors_body",
      "/dashboard"
    ),

    // 6 – Baumgesundheit
    step(
      '[data-tour="dashboard-health"]',
      "dashboard_health_title",
      "dashboard_health_body",
      "/dashboard"
    ),

    // 7 – Aufgaben nach Zeitraum
    step(
      '[data-tour="dashboard-tasks"]',
      "dashboard_tasks_title",
      "dashboard_tasks_body",
      "/dashboard"
    ),

    // 8 – Navigate to Trees
    navStep("/trees", "nav_trees_title", "nav_trees_body"),

    // 9 – Tree list
    step('[data-tour="tree-list"]', "tree_list_title", "tree_list_body", "/trees"),

    // 10 – Add tree button
    step('[data-tour="tree-add-btn"]', "tree_add_title", "tree_add_body", "/trees"),

    // 11 – Navigate to Tree Map
    navStep("/map", "nav_map_title", "nav_map_body"),

    // 12 – Tree map intro
    {
      selector: "body",
      bypassElem: true,
      position: "center",
      content: <StepContent title={c.tree_map_title} body={c.tree_map_body} />,
      action: () => {
        if (window.location.pathname !== "/map") {
          closeSidebar();
          navigate("/map");
        }
      },
    },

    // 13 – Switch to satellite layer (clicks the Satellit radio inside Leaflet LayersControl)
    {
      selector: ".leaflet-control-layers",
      content: <StepContent title={c.map_satellite_title} body={c.map_satellite_body} />,
      action: () => {
        if (window.location.pathname !== "/map") {
          closeSidebar();
          navigate("/map");
        }
        // Expand the layers control if collapsed, then click the satellite radio
        const toggle = document.querySelector(".leaflet-control-layers-toggle");
        if (toggle) toggle.click();
        setTimeout(() => {
          const labels = document.querySelectorAll(".leaflet-control-layers-base label");
          labels.forEach((label) => {
            if (label.textContent.trim() === "Satellit") {
              label.querySelector("input[type='radio']")?.click();
            }
          });
        }, 200);
      },
    },

    // 14 – Add tree button
    {
      selector: '[data-tour="map-add-tree-btn"]',
      content: <StepContent title={c.map_add_tree_title} body={c.map_add_tree_body} />,
      action: () => {
        if (window.location.pathname !== "/map") {
          closeSidebar();
          navigate("/map");
        }
      },
    },

    // 15 – Navigate to Sensors
    navStep("/sensors", "nav_sensors_title", "nav_sensors_body"),

    // 16 – Sensor list
    step('[data-tour="sensor-list"]', "sensor_list_title", "sensor_list_body", "/sensors"),

    // 17 – Project filter: open dropdown and click first project item
    {
      selector: '[data-tour="project-filter"]',
      content: <StepContent title={c.project_filter_title} body={c.project_filter_body} />,
      action: () => {
        const toggle = document.querySelector('[data-tour="project-filter"]');
        if (!toggle) return;
        toggle.click(); // opens the dropdown
        setTimeout(() => {
          // click the first project item inside the now-open dropdown menu
          document.querySelector(".dropdown-menu.show .project-info")?.parentElement?.click();
        }, 300);
      },
    },

    // 18 – Navigate to Data Analysis
    navStep("/data-science", "nav_datascience_title", "nav_datascience_body"),

    // 19 – DS left filter panel overview
    {
      selector: '[data-tour="datascience-filters"]',
      content: (
        <StepContent title={c.datascience_filters_title} body={c.datascience_filters_body} />
      ),
      action: () => {
        if (window.location.pathname !== "/data-science") {
          closeSidebar();
          navigate("/data-science");
        }
        document.querySelector(".ds-top-btn--left")?.click();
      },
    },

    // 20 – DS filter: select area (click first area checkbox)
    {
      selector: '[data-tour="ds-filter-areas"]',
      content: <StepContent title={c.ds_filter_areas_title} body={c.ds_filter_areas_body} />,
      action: () => {
        if (window.location.pathname !== "/data-science") {
          closeSidebar();
          navigate("/data-science");
        }
        document
          .querySelector('[data-tour="ds-filter-areas"] .filter__list input[type="checkbox"]')
          ?.click();
      },
    },

    // 21 – DS filter: select tree (click first tree checkbox)
    {
      selector: '[data-tour="ds-filter-trees"]',
      content: <StepContent title={c.ds_filter_trees_title} body={c.ds_filter_trees_body} />,
      action: () => {
        if (window.location.pathname !== "/data-science") {
          closeSidebar();
          navigate("/data-science");
        }
        document
          .querySelector('[data-tour="ds-filter-trees"] .filter__list input[type="checkbox"]')
          ?.click();
      },
    },

    // 22 – DS filter: select sensor (click first sensor checkbox)
    {
      selector: '[data-tour="ds-filter-sensors"]',
      content: <StepContent title={c.ds_filter_sensors_title} body={c.ds_filter_sensors_body} />,
      action: () => {
        if (window.location.pathname !== "/data-science") {
          closeSidebar();
          navigate("/data-science");
        }
        document
          .querySelector('[data-tour="ds-filter-sensors"] .filter__list input[type="checkbox"]')
          ?.click();
      },
    },

    // 23 – DS filter: select variable (click first variable checkbox)
    {
      selector: '[data-tour="ds-filter-variables"]',
      content: (
        <StepContent title={c.ds_filter_variables_title} body={c.ds_filter_variables_body} />
      ),
      action: () => {
        if (window.location.pathname !== "/data-science") {
          closeSidebar();
          navigate("/data-science");
        }
        document
          .querySelector('[data-tour="ds-filter-variables"] .filter__list input[type="checkbox"]')
          ?.click();
      },
    },

    // 24 – DS right date filter
    {
      selector: '[data-tour="datascience-date-filter"]',
      content: <StepContent title={c.datascience_date_title} body={c.datascience_date_body} />,
      action: () => {
        if (window.location.pathname !== "/data-science") {
          closeSidebar();
          navigate("/data-science");
        }
        document.querySelector(".ds-top-btn--right")?.click();
      },
    },

    // 25 – Load data (highlight button below, click it) (highlight button, click it, popover appears below)
    {
      selector: '[data-tour="ds-load-data"]',
      position: "bottom",
      content: <StepContent title={c.ds_load_data_title} body={c.ds_load_data_body} />,
      action: () => {
        if (window.location.pathname !== "/data-science") {
          closeSidebar();
          navigate("/data-science");
        }
        document.querySelector('[data-tour="ds-load-data"]')?.click();
      },
    },

    // 26 – Navigate to Tasks
    navStep("/tasks", "nav_tasks_title", "nav_tasks_body"),

    // 27 – Task view header (bypassElem – header is inside ReactTable in list mode)
    {
      selector: "body",
      bypassElem: true,
      position: "center",
      content: <StepContent title={c.task_header_title} body={c.task_header_body} />,
      action: () => {
        if (window.location.pathname !== "/tasks") {
          closeSidebar();
          navigate("/tasks");
        }
      },
    },

    // 28 – Calendar button
    step('[data-tour="task-calendar-btn"]', "task_calendar_title", "task_calendar_body", "/tasks"),

    // 29 – Add task button
    step('[data-tour="task-add-btn"]', "task_add_title", "task_add_body", "/tasks"),

    // 30 – Navigate to Projects
    navStep("/projects", "nav_projects_title", "nav_projects_body"),

    // 31 – Projects grid (unchanged)
    step('[data-tour="projects-page"]', "projects_grid_title", "projects_grid_body", "/projects"),

    // 32 – Open first project
    {
      selector: "body",
      bypassElem: true,
      position: "center",
      content: <StepContent title={c.projects_open_title} body={c.projects_open_body} />,
      action: () => {
        if (window.location.pathname !== "/projects") {
          closeSidebar();
          navigate("/projects");
        }
        document.querySelector('[data-testid="project-detail-btn"]')?.click();
      },
    },

    // 33 – Navigate to Settings
    navStep("/settings/notifications", "nav_settings_title", "nav_settings_body"),

    // 34 – Settings overview
    step(
      '[data-tour="settings-page"]',
      "settings_page_title",
      "settings_page_body",
      "/settings/notifications"
    ),

    // 35 – Navigate to Settings / Users
    navStep("/settings/users", "nav_settings_users_title", "nav_settings_users_body"),

    // 36 – Users management overview
    step(
      '[data-tour="settings-users"]',
      "settings_users_title",
      "settings_users_body",
      "/settings/users"
    ),

    // 37 – Add user button (clicks it to open the form)
    {
      selector: '[data-tour="settings-add-user-btn"]',
      content: (
        <StepContent title={c.settings_add_user_btn_title} body={c.settings_add_user_btn_body} />
      ),
      action: () => {
        if (window.location.pathname !== "/settings/users") {
          closeSidebar();
          navigate("/settings/users");
        }
        document.querySelector('[data-tour="settings-add-user-btn"] button')?.click();
      },
    },

    // 38 – Persona: Tree Inspector
    {
      selector: '[data-tour="user-persona-select"]',
      content: (
        <StepContent title={c.user_persona_inspector_title} body={c.user_persona_inspector_body} />
      ),
      action: () => {
        if (window.location.pathname !== "/settings/users") {
          closeSidebar();
          navigate("/settings/users");
        }
        const sel = document.querySelector('[data-tour="user-persona-select"] select');
        if (sel) {
          sel.value = "tree_inspector";
          sel.dispatchEvent(new Event("change", { bubbles: true }));
        }
      },
    },

    // 39 – Persona: City Tree Manager
    {
      selector: '[data-tour="user-persona-select"]',
      content: (
        <StepContent title={c.user_persona_manager_title} body={c.user_persona_manager_body} />
      ),
      action: () => {
        if (window.location.pathname !== "/settings/users") {
          closeSidebar();
          navigate("/settings/users");
        }
        const sel = document.querySelector('[data-tour="user-persona-select"] select');
        if (sel) {
          sel.value = "city_tree_manager";
          sel.dispatchEvent(new Event("change", { bubbles: true }));
        }
      },
    },

    // 40 – Finish
    {
      selector: "body",
      bypassElem: true,
      position: "center",
      content: <StepContent title={c.finish_title} body={c.finish_body} />,
    },
  ];
}
